import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Grid,
  Snackbar,
  Alert,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { RoleContext } from 'src/context/roleContext';
import { ADMIN, SKOLA } from 'src/constants/role';
import { ListboxComponent } from 'src/scenes/users/components/helperFunction';
// Import tvoje funkcije za konverziju
import { latinToCyrillic } from 'src/helper/helperFunction';

const ImportStudentsDialog = ({
  open,
  handleClose,
  loggedInSchool,
  onSuccess
}) => {
  const { t } = useTranslation();
  const { role } = useContext(RoleContext);

  // State variables
  const [file, setFile] = useState(null);
  const [schoolData, setSchoolData] = useState({
    name: '',
    street: '',
    city: '',
    zipcode: '',
    teacher: '',
    phone: '',
    email: ''
  });
  const [studentsData, setStudentsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [schoolExists, setSchoolExists] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [schoolId, setSchoolId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);

  const isSchoolRole = role === SKOLA;

  // Columns for the DataGrid
  const columns = [
    { field: 'name', headerName: t('student.name'), width: 200 },
    { field: 'category', headerName: t('student.category'), width: 100 },
    { field: 'grade', headerName: t('student.grade'), width: 100 },
    { field: 'language', headerName: t('student.language'), width: 250 },
    { field: 'gender', headerName: t('student.gender'), width: 100 }
  ];

  // Reset all data
  const resetData = () => {
    setSchoolData({
      name: '',
      street: '',
      city: '',
      zipcode: '',
      teacher: '',
      phone: '',
      email: ''
    });
    setStudentsData([]);
    setFile(null);
    setSchoolExists(null);
    setSchoolId(null);
    setSelectedSchool(null);
  };

  // Pomocna funkcija za normalizaciju imena (ako želiš da ukloniš OŠ, navodnike itd.)
  const normalizeSchoolName = (name) => {
    // Pretvori ime iz latinice u ćirilicu
    const nameCyrillic = latinToCyrillic(name.toLowerCase());
    // Ukloni "oš", "os", "ош", "ос" i slično
    const removePrefix = nameCyrillic.replace(/(oš|os|ош|ос)/gi, '');
    // Dodatno ukloni navodnike, razmake itd.
    return removePrefix.replace(/[„”"']/g, '').replace(/[\s-]/g, '');
  };

  // Nova funkcija za čitanje Excel fajla
  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    setFile(uploadedFile);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Dobijanje imena škole iz fajla (uobičajeno na 2. redu, kolona E u tvom primeru)
      const importedSchoolName = jsonData[2]?.[4] || '';

      // Ako je ulogovani korisnik rola SKOLA - proveri da li se ime iz fajla poklapa sa imenom škole
      if (isSchoolRole && loggedInSchool) {
        // Upoređujemo normalizovana imena
        if (
          normalizeSchoolName(importedSchoolName) !==
          normalizeSchoolName(loggedInSchool.name)
        ) {
          setErrorMessage(t('errors.schoolNameMismatch'));
          setFile(null);
          return;
        }

        // Čitanje ostalih podataka iz Excel fajla (profesor, telefon, email)
        const schoolInfo = {
          teacher: jsonData[4]?.[4] || '',
          phone: jsonData[5]?.[4] || '',
          email: jsonData[6]?.[4] || ''
        };

        // Preuzimanje podataka o školi iz baze za ostala polja (street, city, zipcode)
        try {
          const res = await axios.get(
            `${API_BASE_URL}/api/schools.php?id=${loggedInSchool.id}`
          );
          const schoolInfoFromDB = res.data;
          setSchoolData({
            id: schoolInfoFromDB.id || '',
            name: schoolInfoFromDB.name || '',
            street: schoolInfoFromDB.street || '',
            city: schoolInfoFromDB.location_name || '',
            zipcode: schoolInfoFromDB.zipcode || '',
            teacher: schoolInfo.teacher || schoolInfoFromDB.professor || '',
            phone: schoolInfo.phone || schoolInfoFromDB.telephone || '',
            email: schoolInfo.email || schoolInfoFromDB.email || ''
          });
          setSchoolId(loggedInSchool.id);
          // Prikaz poruke da mogu da izmene podatke o školi ako nisu tačni
          setSnackbarMessage(t('messages.canEditSchoolData'));
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
        } catch (error) {
          console.error('Error fetching school data:', error);
        }
      } else if (role === ADMIN) {
        // Ako je admin, preuzimamo podatke iz Excel fajla
        const schoolInfo = {
          name: importedSchoolName,
          address: jsonData[3]?.[4] || '',
          teacher: jsonData[4]?.[4] || '',
          phone: jsonData[5]?.[4] || '',
          email: jsonData[6]?.[4] || ''
        };

        // address je tipa "ulica, grad, ZIP", pa probaj da parsiraš
        const [street = '', city = '', zipcode = ''] =
          schoolInfo.address.split(',');

        setSchoolData({
          name: schoolInfo.name ? schoolInfo.name.trim() : '',
          street: street.trim(),
          city: city.trim(),
          zipcode: zipcode.trim(),
          teacher: schoolInfo.teacher,
          phone: schoolInfo.phone,
          email: schoolInfo.email
        });
      }

      // Parse students data: obično počinje od 11. reda
      const students = [];
      for (let i = 11; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (row && row.length > 0) {
          students.push({
            id: i - 11,
            name: row[1] || '',
            category: row[2] || '',
            grade: row[3] || '',
            language: row[4] || '',
            gender: row[5] || ''
          });
        }
      }

      setStudentsData(students);
      setErrorMessage('');
    };

    reader.readAsArrayBuffer(uploadedFile);
  };

  // Fetch all schools if role is admin
  useEffect(() => {
    if (role === ADMIN) {
      axios
        .get(`${API_BASE_URL}/api/schools.php?allSchoolsWithLocation`)
        .then((response) => {
          setSchools(response.data);
        })
        .catch((error) => {
          console.error('Error fetching schools:', error);
        });
    }
  }, [role]);

  /**
   * validateName: pokušava da pronađe tačnu školu u bazi.
   * 1. Dohvati sve škole koje se poklapaju samo po imenu (API: school_name=...).
   * 2. Filtriraj po ulici, gradu i poštanskom broju.
   * 3. Ako je tačno 1 rezultat - setuje `schoolExists` na true i pamti ID.
   * 4. Ako ih je 0 ili >1 - `setSchoolExists(false)`.
   */
  const validateName = async ({ name, street, city, zipcode }) => {
    try {
      // 1) Pozovi API samo po imenu (kao do sada)
      const res = await axios.get(
        `${API_BASE_URL}/api/schools.php?school_name=${encodeURIComponent(
          name
        )}`
      );

      if (!res.data || res.data.length === 0) {
        // Nema ni jedne škole s tim imenom
        setSchoolExists(false);
        return;
      }

      // 2) Normalizuj vrednosti i filtriraj
      const streetNormalized = latinToCyrillic(street.toLowerCase().trim());
      const cityNormalized = latinToCyrillic(city.toLowerCase().trim());
      const zipNormalized = zipcode.trim();

      let possibleMatches = res.data;

      // Filtriraj po ulici (ako je korisnik nešto uneo)
      if (streetNormalized) {
        possibleMatches = possibleMatches.filter((sch) => {
          const schStreet = latinToCyrillic((sch.street || '').toLowerCase());
          // Možeš da koristiš includes() ili ===, po želji
          return schStreet.includes(streetNormalized);
        });
      }

      // Filtriraj po gradu (ako ima)
      if (cityNormalized) {
        possibleMatches = possibleMatches.filter((sch) => {
          const schCity = latinToCyrillic(
            (sch.location_name || '').toLowerCase()
          );
          return schCity.includes(cityNormalized);
        });
      }

      // Filtriraj po ZIP-u
      if (zipNormalized) {
        possibleMatches = possibleMatches.filter((sch) => {
          return (sch.zipcode || '').trim() === zipNormalized;
        });
      }

      // 3) Vidi koliko je ostalo
      if (possibleMatches.length === 1) {
        // Tačno jedna škola
        setSchoolExists(true);
        setSchoolId(possibleMatches[0].id);
      } else {
        // 0 ili više od 1
        setSchoolExists(false);
      }
    } catch (error) {
      console.error('Error validating school name:', error);
      setSchoolExists(false);
    }
  };

  // useEffect za validaciju škole - kad se promeni ime, ulica, grad ili zip
  useEffect(() => {
    // Ako je prazno ime, resetuj
    if (schoolData.name.trim() === '') {
      setSchoolExists(null);
      return;
    }
    // Inače pokušaj da validiraš
    validateName({
      name: schoolData.name.trim(),
      street: schoolData.street,
      city: schoolData.city,
      zipcode: schoolData.zipcode
    });
  }, [schoolData.name, schoolData.street, schoolData.city, schoolData.zipcode]);

  // Handle saving data (school and students)
  const handleSave = async () => {
    try {
      setIsLoading(true);

      let currentSchoolId = schoolId; //schoolData.id;
      if (loggedInSchool && loggedInSchool.id) {
        currentSchoolId = loggedInSchool.id;
      }

      if (schoolExists) {
        // Škola postoji u bazi -> radimo PUT (update)
        const updateSchoolPayload = {
          id: currentSchoolId,
          name: schoolData.name,
          // Po želji ovde ažuriraš i location/zipcode
          street: schoolData.street,
          telephone: schoolData.phone,
          email: schoolData.email,
          professor: schoolData.teacher
        };
        await axios.put(
          `${API_BASE_URL}/api/schools.php?id=${schoolId}`,
          updateSchoolPayload
        );
      } else {
        // Škola nije pronađena -> pravimo novu
        const newSchoolPayload = {
          name: schoolData.name,
          type: '1',
          street: schoolData.street,
          telephone: schoolData.phone,
          email: schoolData.email,
          professor: schoolData.teacher
        };
        const res = await axios.post(
          `${API_BASE_URL}/api/schools.php`,
          newSchoolPayload
        );
        currentSchoolId = res.data.id;
        setSchoolId(currentSchoolId);
      }

      const genderMap = {
        женски: 'z',
        мушки: 'm'
      };

      // Dodaj studente
      for (let student of studentsData) {
        const mappedGender =
          genderMap[student.gender.toLowerCase()] || 'unknown';
        if (mappedGender === 'unknown') {
          console.warn(
            `Nepoznati pol za studenta: ${student.name}. Pol: ${student.gender}`
          );
          // Nastavi, ili setuj default:
          continue;
        }
        const studentPayload = {
          schoolid: currentSchoolId,
          name: student.name,
          pol: mappedGender,
          category: student.category,
          class: student.grade,
          professor: schoolData.teacher,
          language: student.language
        };
        await axios.post(`${API_BASE_URL}/api/students.php`, studentPayload);
      }

      setSnackbarMessage(t('messages.studentsAdded'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      if (onSuccess) {
        onSuccess();
      }

      setIsLoading(false);
      resetData();
      handleClose();
    } catch (error) {
      console.error('Error saving data:', error);
      setSnackbarMessage(t('errors.saveDataError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsLoading(false);
    }
  };

  // Handle input changes for school data
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSchoolData({
      ...schoolData,
      [name]: value
    });
  };

  // Handle school change iz autocomplete (kad je ADMIN)
  const handleSchoolChange = async (event, newValue) => {
    setSelectedSchool(newValue);
    if (newValue) {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/schools.php?id=${newValue.id}`
        );
        const schoolInfo = res.data;
        setSchoolData({
          name: schoolInfo.name || '',
          street: schoolInfo.street || '',
          city: schoolInfo.location_name || '',
          zipcode: schoolInfo.zipcode || '',
          teacher: schoolInfo.professor || '',
          phone: schoolInfo.telephone || '',
          email: schoolInfo.email || ''
        });
        setSchoolId(newValue.id);
      } catch (error) {
        console.error('Error fetching school data:', error);
      }
    } else {
      setSchoolData({
        name: '',
        street: '',
        city: '',
        zipcode: '',
        teacher: '',
        phone: '',
        email: ''
      });
      setSchoolId(null);
    }
  };

  // Filter funkcija za autocomplete da omogući pretragu i po ćirilici
  const filterSchool = (options, { inputValue }) => {
    const cyrillicInput = latinToCyrillic(inputValue.toLowerCase());
    return options.filter((option) =>
      latinToCyrillic(option.name.toLowerCase()).includes(cyrillicInput)
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        resetData();
        handleClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{t('importStudentsDialog.title')}</DialogTitle>

      <DialogContent>
        {!file && (
          <Box>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}
            <Typography>{t('importStudentsDialog.uploadPrompt')}</Typography>
            <input
              type="file"
              accept=".xlsx, .xls"
              id="fileUpload"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <label htmlFor="fileUpload">
              <Button
                variant="outlined"
                component="span"
                sx={{
                  marginTop: '10px',
                  padding: '6px 16px',
                  borderRadius: '4px',
                  borderColor: '#ccc',
                  color: '#333',
                  backgroundColor: '#f5f5f5',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                    borderColor: '#bbb'
                  },
                  width: 'auto',
                  cursor: 'pointer'
                }}
              >
                {t('buttons.chooseFile')}
              </Button>
            </label>
            <Typography
              variant="body2"
              sx={{ marginTop: '8px', color: '#666' }}
            >
              {file ? file.name : t('buttons.noFileChosen')}
            </Typography>
          </Box>
        )}

        {isLoading && <CircularProgress />}

        {file && !isLoading && (
          <Box>
            {studentsData.length === 0 ? (
              <Alert severity="warning">
                {t('importStudentsDialog.noStudentsInExcel')}
              </Alert>
            ) : (
              <>
                {role === ADMIN && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('importStudentsDialog.schoolCanBeChanged')}
                  </Alert>
                )}

                {/* Autocomplete za izbor škole (samo ako je ADMIN) */}
                {role === ADMIN && (
                  <Autocomplete
                    options={schools}
                    getOptionLabel={(option) =>
                      `${option.name} - ${option.location_name}`
                    }
                    value={selectedSchool}
                    ListboxComponent={ListboxComponent}
                    onChange={handleSchoolChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('dialog.selectSchool')}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                      />
                    )}
                    filterOptions={filterSchool}
                    noOptionsText={t('pagination.noRowsLabel')}
                  />
                )}

                {/* Ako utvrdimo da škola ne postoji po imenu/adresi */}
                {!schoolExists && schoolExists !== null && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('errors.schoolNotExistWarning')}
                  </Alert>
                )}

                {/* Polja za prikaz/izmenu podataka o školi */}
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('school.name')}
                        value={schoolData.name}
                        name="name"
                        fullWidth
                        margin="normal"
                        onChange={handleInputChange}
                        InputProps={{
                          style: { fontWeight: 'bold' },
                          readOnly: isSchoolRole,
                          endAdornment:
                            schoolExists !== null &&
                            schoolData.name.trim() !== '' ? (
                              <InputAdornment position="end">
                                {schoolExists ? (
                                  <CheckCircleIcon color="success" />
                                ) : (
                                  <ErrorIcon color="error" />
                                )}
                              </InputAdornment>
                            ) : null
                        }}
                        sx={{
                          backgroundColor: isSchoolRole ? '#e0e0e0' : '#f5f7fa'
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('school.teacher')}
                        value={schoolData.teacher}
                        name="teacher"
                        fullWidth
                        margin="normal"
                        onChange={handleInputChange}
                        sx={{
                          backgroundColor: '#f5f7fa'
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('school.street')}
                        value={schoolData.street}
                        name="street"
                        fullWidth
                        margin="normal"
                        onChange={handleInputChange}
                        InputProps={{
                          readOnly: isSchoolRole
                        }}
                        sx={{
                          backgroundColor: isSchoolRole ? '#e0e0e0' : '#f5f7fa'
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <TextField
                        label={t('school.city')}
                        value={schoolData.city}
                        name="city"
                        fullWidth
                        margin="normal"
                        onChange={handleInputChange}
                        InputProps={{
                          readOnly: isSchoolRole
                        }}
                        sx={{
                          backgroundColor: isSchoolRole ? '#e0e0e0' : '#f5f7fa'
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <TextField
                        label={t('school.zipcode')}
                        value={schoolData.zipcode}
                        name="zipcode"
                        fullWidth
                        margin="normal"
                        onChange={handleInputChange}
                        InputProps={{
                          readOnly: isSchoolRole
                        }}
                        sx={{
                          backgroundColor: isSchoolRole ? '#e0e0e0' : '#f5f7fa'
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('school.phone')}
                        value={schoolData.phone}
                        name="phone"
                        fullWidth
                        margin="normal"
                        onChange={handleInputChange}
                        sx={{
                          backgroundColor: '#f5f7fa'
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('school.email')}
                        value={schoolData.email}
                        name="email"
                        fullWidth
                        margin="normal"
                        onChange={handleInputChange}
                        InputProps={{
                          readOnly: isSchoolRole
                        }}
                        sx={{
                          backgroundColor: isSchoolRole ? '#e0e0e0' : '#f5f7fa'
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Spisak učenika iz .xlsx */}
                {studentsData.length > 0 && (
                  <Box mt={4} style={{ padding: '2px', marginTop: '2px' }}>
                    <Typography variant="h6" gutterBottom>
                      {t('importStudentsDialog.studentsList')}
                    </Typography>
                    <div style={{ height: 400, width: '100%' }}>
                      <DataGrid
                        rows={studentsData}
                        columns={columns}
                        pageSize={5}
                        rowsPerPageOptions={[5]}
                        disableSelectionOnClick
                        sx={{
                          boxShadow: 2,
                          border: 2,
                          borderColor: '#2596be',
                          '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#2596be',
                            color: '#fff',
                            fontWeight: 'bold',
                            textAlign: 'center'
                          },
                          '& .MuiDataGrid-cell': {
                            padding: '12px',
                            textAlign: 'center',
                            fontSize: '14px'
                          },
                          '& .MuiDataGrid-footerContainer': {
                            backgroundColor: '#f5f5f5',
                            borderTop: '1px solid #e0e0e0',
                            padding: '10px'
                          }
                        }}
                      />
                    </div>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            resetData();
            handleClose();
          }}
          sx={{
            backgroundColor: '#cccccc',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#bbbbbb'
            }
          }}
        >
          {t('buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={
            !file || isLoading || !studentsData.length || schoolExists === null
          }
          sx={{
            backgroundColor: '#2596be',
            '&:hover': {
              backgroundColor: '#1c75bc'
            },
            color: '#ffffff',
            padding: '8px 16px',
            fontWeight: 'bold',
            borderRadius: '8px'
          }}
        >
          {t('buttons.save')}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ImportStudentsDialog;
